import { useCallback, useMemo, useState } from "react";
import { Chart, ChartTypes } from "@uicore";
import {
  Card,
  CardContent,
  CardHeader,
  NativeSelect,
  Skeleton,
  Stack,
  Typography,
} from "@altimateai/lego";
import { Line } from "recharts";
import { VectorSearchEndpointTrendPoint } from "../types";
import { formatCost } from "@/helpers/utils";
import { formatNumber } from "@/uiCore/components/formatters/format";
import { useAppState } from "@/modules/app/useAppContext";
import { formatShortDate } from "../utils";

type GatewayMetric = "cost" | "dbus";

const METRIC_OPTIONS: { value: GatewayMetric; label: string }[] = [
  { value: "cost", label: "Cost" },
  { value: "dbus", label: "DBUs" },
];

const METRIC_STROKE: Record<GatewayMetric, string> = {
  cost: "#3B82F6",
  dbus: "#10b981",
};

interface Props {
  data: VectorSearchEndpointTrendPoint[];
  isLoading: boolean;
}

export const AiGatewayEndpointTrendChart = ({ data, isLoading }: Props) => {
  const [metric, setMetric] = useState<GatewayMetric>("cost");
  const { currency } = useAppState();

  const chartData = useMemo(
    () =>
      data.map(point => ({
        date: point.date,
        value: metric === "cost" ? point.cost_usd : point.dbus,
      })),
    [data, metric]
  );

  const formatValue = useCallback(
    (value: number): string =>
      metric === "cost" ? formatCost(value, currency) : formatNumber(value),
    [metric, currency]
  );

  const title = metric === "cost" ? "Daily Cost Trend" : "Daily DBU Usage";
  const seriesName = metric === "cost" ? "Cost" : "DBUs";

  return (
    <Card>
      <CardHeader>
        <Stack direction="row" align="center" justify="between" className="al-w-full">
          <Typography variant="section-heading">{title}</Typography>
          <Stack direction="row" align="center" gap="xs">
            <Typography size="sm" className="al-text-gray-500">
              Metric
            </Typography>
            <NativeSelect
              aria-label="Select metric"
              value={metric}
              onChange={(value: string) => {
                if (value === "cost" || value === "dbus") {
                  setMetric(value);
                }
              }}
              variant="outline"
              size="sm"
              options={METRIC_OPTIONS}
            />
          </Stack>
        </Stack>
      </CardHeader>
      <CardContent>
        <div className="al-pb-10">
          {isLoading ? (
            <Skeleton className="al-h-[380px] al-w-full" />
          ) : data.length === 0 ? (
            <Stack direction="column" align="center" justify="center" className="al-h-[380px]">
              <Typography className="al-text-gray-500">
                No trend data for selected period
              </Typography>
            </Stack>
          ) : (
            <Chart
              title=""
              xAxisDataKey="date"
              yAxisLabel={{ value: metric === "cost" ? "Cost" : "DBUs" }}
              xAxisLabelFormatter={formatShortDate}
              yAxisLabelFormatter={(value: string) => formatValue(Number(value))}
              type={ChartTypes.LineChart}
              data={chartData}
              height={380}
              legendProps={{ wrapperStyle: { bottom: -16, paddingTop: 4, fontSize: 12 } }}
            >
              {() => (
                <Line
                  type="monotone"
                  name={seriesName}
                  dataKey="value"
                  stroke={METRIC_STROKE[metric]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              )}
            </Chart>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
